import { Box, Button, Grid, MenuItem, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GridSortModel } from '@mui/x-data-grid';
import { CancelTokenSource } from 'axios';
import { Formik } from 'formik';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import * as Yup from 'yup';

import { LabelledOutline } from '../../../common/final-form-material-ui/LabelledOutline';
import {
  FormikSelect,
  FormikTextField,
} from '../../../common/formik-material-ui';
import { IUserAtom, Role, user } from '../../../recoil/atoms/user';
import {
  IPermissions,
  permissionsSelector,
} from '../../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import { endpointEnum } from '../../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';

import { ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    marginLeft: theme.spacing(1),
  },
}));

export interface IEmailReport {
  reportType: 'email';
  businessId: number;
  kixieReportName:
    | 'call-history'
    | 'sms-history'
    | 'powerlist-contacts'
    | 'kixie-numbers';
  fromDate: Date | string;
  toDate: Date | string;
  lastExportURL?: string;
  emailList: string[];
  userId?: number | string | null | undefined;
  powerlistId?: number;
  filters?: PowerlistContactFilters | { search: string };
  submittingUser?: number;
  superadmin?: boolean;
}

const validationSchema = Yup.object({
  page: Yup.string().required('Required'),
  recipients: Yup.array().when('page', {
    is: (page) => page === 'all',
    then: Yup.array()
      .required('Required')
      .transform(function (value, originalValue) {
        if (this.isType(value) && value !== null) {
          return value;
        }
        return originalValue ? originalValue.split(/[\s,]+/) : [];
      })
      .of(Yup.string().email(({ value }) => `${value} is not a valid email`)),
  }),
});

interface PowerlistContactFilters {
  search: string;
  status: string;
  sortModel: GridSortModel;
}

const HistoryExportModal = (props: {
  show: boolean;
  handleClose: () => void;
  page: number;
  pageSize: number;
  totalRows: number;
  type: IEmailReport['kixieReportName'];
  csvProps: Record<string, any>;
  agentId?: number;
  rows: { id: number; status: string; crmlink?: string }[];
  powerlistId?: number;
  filters: PowerlistContactFilters | { search: string };
  canDownloadHistoryBoolean?: boolean;
}) => {
  const classes = useStyles();
  const {
    page,
    pageSize,
    totalRows,
    csvProps,
    type,
    agentId,
    rows,
    canDownloadHistoryBoolean = true,
  } = props;
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const route = useLocation();
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents as IPermissions;
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [, setUserAtom] = useRecoilStateLoadable(user);
  const [, setDeleteCancelTokenSource] = React.useState<
    CancelTokenSource | undefined
  >();
  const [numberOfPages, setNumberOfPages] = useState<number | string>(0);
  const [start, setStart] = useState<number | string>(0);
  const [end, setEnd] = useState<number | string>(0);
  const isAgentLevel =
    route.pathname.includes('/my-profile/') ||
    route.pathname.includes('/manage/agents/');
  useEffect(() => {
    setNumberOfPages(Math.floor((totalRows + pageSize - 1) / pageSize));
    const start = page * pageSize - (pageSize - 1);
    const end = start + pageSize - 1;
    setStart(start);
    setEnd(end);
  }, [totalRows, pageSize, page]);

  const mutation = useMutation(
    getApiMutation(userAtomContents, setUserAtom, setDeleteCancelTokenSource)
  );
  const getEmailEndpoint = () =>
    userAtomContents.businessid
      ? endpointEnum.EMAIL_REPORTS.replace(
          '{bid}',
          userAtomContents.businessid.toString()
        ).replace(`{bid}`, userAtomContents.businessid?.toString() as string)
      : '';

  const dataToDownload = useMemo(() => {
    if (csvProps?.csvData?.length > 0 && rows?.length > 0) {
      const res = csvProps?.csvData?.slice();
      if (type !== 'kixie-numbers') {
        res.map((el, i) => {
          if (i === 0) {
            el?.push('crm link');
          } else {
            el?.push(rows?.[i - 1]?.crmlink ?? '');
          }
        });
      }
      return res;
    } else {
      return csvProps?.csvData ?? '';
    }
  }, [rows, csvProps?.csvData, type]);
  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={async (values, actions) => {
        if (
          !canDownloadHistoryBoolean &&
          (type === 'call-history' || type === 'sms-history')
        ) {
          enqueueSnackbarWithStyles({
            message: `You do not have permission to download ${type}`,
            alertProps: { severity: 'error' },
          });
          props.handleClose();
          return;
        }
        try {
          const data: IEmailReport = {
            reportType: 'email',
            businessId: userAtomContents.businessid,
            kixieReportName: type,
            fromDate: csvProps?.start,
            toDate: csvProps?.end,
            emailList: !Array.isArray(values?.recipients)
              ? String(values?.recipients ?? '')
                  .split(',')
                  .map((r) => r.trim())
              : values?.recipients,
            submittingUser: userAtomContents?.userid,
            // lastExportURL?: string,
            superadmin: role === Role.SUPERADMIN,
          };
          if (isAgentLevel && agentId) {
            data.userId = agentId;
          }
          if (props.powerlistId) {
            data.powerlistId = props.powerlistId;
          }
          if (props.filters) {
            data.filters = props.filters;
          }
          const response = await mutation.mutateAsync({
            endpoint: getEmailEndpoint(),
            domain: process.env.VITE_APP_CDK_DOMAIN,
            stage: 'itn',
            method: 'POST',
            payload: data,
          });
          if (response?.status === 200 || response?.status === 201) {
            enqueueSnackbarWithStyles({
              message: response?.data || 'Email report successfully queued',
              alertProps: { severity: 'success' },
            });
            props.handleClose();
          }
        } catch (err) {
          enqueueSnackbarWithStyles({
            message: `Error adding report to queue`,
            alertProps: { severity: 'error' },
          });
          props.handleClose();
        }
        actions.resetForm();
      }}
      initialValues={{
        page: 'all', // Set the default value to 'all'
        recipients: [] as any[],
      }}
    >
      {(formik) => (
        <ModalWithStyles
          onSubmit={formik.handleSubmit}
          title={
            type === 'call-history'
              ? 'Export Call History'
              : type === 'sms-history'
              ? 'Export SMS History'
              : type === 'powerlist-contacts'
              ? 'Export PowerList Contacts'
              : 'Export Kixie Numbers'
          }
          aria-labelledby="example-modal-sizes-title-lg"
          show={props.show}
          onHide={() => {
            props.handleClose();
            formik.resetForm({});
          }}
          isSubmitting={formik.isSubmitting}
          isLoading={formik.isSubmitting}
          size="sm"
          disableSubmit={!formik.isValid || formik.isSubmitting}
          footerComponent={
            <div style={{ justifyContent: 'flex-end' }}>
              <Button
                className={classes.button}
                disableElevation
                variant="contained"
                disabled={formik.isSubmitting}
                onClick={() => props?.handleClose()}
                color="inherit"
              >
                Cancel
              </Button>
              {formik?.values?.page === 'current' ? (
                <CSVLink
                  filename={`Kixie-${csvProps?.dataType}-${moment(
                    csvProps?.start
                  ).format('YYYY-MM-DD')} - ${moment(csvProps?.end).format(
                    'YYYY-MM-DD'
                  )}`}
                  data={dataToDownload ?? ''}
                >
                  <Button
                    className={classes.button}
                    style={{ marginRight: '10px' }}
                    disableElevation
                    variant="contained"
                    color="primary"
                    onClick={() => props.handleClose()}
                    disabled={formik.isSubmitting}
                  >
                    Save
                  </Button>
                </CSVLink>
              ) : (
                <Button
                  className={classes.button}
                  style={{ marginRight: '10px' }}
                  disableElevation
                  variant="contained"
                  color="primary"
                  disabled={!formik.isValid || formik.isSubmitting}
                  onClick={() => formik?.handleSubmit()}
                >
                  Save
                </Button>
              )}
            </div>
          }
        >
          <Box>
            <LabelledOutline label="Export Range">
              <Grid container direction="column">
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <p>
                    Choose a range to export. Select current page to download
                    just the rows on the page you're currently viewing, or
                    select all pages to send an email with all rows on all
                    pages.
                  </p>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <FormikSelect
                    name="page"
                    formik={formik}
                    fullWidth
                    label="Select an option"
                    size="small"
                    variant="outlined"
                    required
                  >
                    <MenuItem value="all">All Pages</MenuItem>
                    <MenuItem value="current">Current Page</MenuItem>
                  </FormikSelect>
                  {formik?.values?.page === 'current' ? (
                    <div>
                      <div>{`${'Page ' + props?.page}`}</div>
                      <div>{`Rows ${start} to ${end} of ${totalRows}`}</div>
                    </div>
                  ) : (
                    <div>
                      <div>{`${'Pages 1 - ' + numberOfPages}`}</div>
                      <div>{`Rows 1 - ${totalRows} of ${totalRows}`}</div>
                    </div>
                  )}
                </Grid>
              </Grid>
            </LabelledOutline>
            <div>
              {formik?.values?.page !== 'all' && (
                <p>
                  Note: The current page spreadsheet with fewer columns than the
                  all pages option. Select all pages to receive a download with
                  more information.
                </p>
              )}
            </div>
            {formik?.values?.page === 'all' && (
              <LabelledOutline label="Export Email(s)">
                <Grid container direction="column">
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <p>
                      Enter the email addresses for each recipient of the data
                      to be exported. separate email addresses with a comma.
                    </p>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                      <FormikTextField
                        name="recipients"
                        formik={formik}
                        fullWidth
                        label="Email Addresses"
                        size="small"
                        variant="outlined"
                        required={formik?.values?.page === 'all'}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <p style={{ marginTop: '12px' }}>
                      Once a range and recipient(s) have been entered, click
                      Save to send a CSV.
                    </p>
                  </Grid>
                </Grid>
              </LabelledOutline>
            )}
          </Box>
        </ModalWithStyles>
      )}
    </Formik>
  );
};

export default HistoryExportModal;
